import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CTABanner from "@/components/CTABanner";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";

const VALUES = [
  { title: "Seafront Setting", desc: "Every room faces the Arabian Sea - ground-floor rooms open onto balconies, upper floors offer large glass viewing windows." },
  { title: "Pilgrim First", desc: "Complimentary shuttle service to Somnath Temple for our guests and senior citizens, and a team that understands a pilgrim's day." },
  { title: "Trust Heritage", desc: "Operated by Shree Somnath Trust, carrying forward a tradition of service to visitors of the sacred Jyotirlinga." },
];

export default function About() {
  useEffect(() => {
    document.title = "About Us | Sagar Darshan Guest House, Somnath Gujarat";
  }, []);

  return (
    <div data-testid="about-page">
      <PageHero overline="About Us" title="Your Home Away From Home"
        image="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/8phsefz9_IMG_0887.jpeg" />

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <div className="grid items-start gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading number="01" overline="Who We Are" title="A Seafront Sanctuary in Somnath" />
            <Reveal delay={0.15}>
              <p className="mt-7 leading-relaxed text-navy/70">
                {SITE.name} stands on the coast at {SITE.address}. {SITE.operator}, our guest house has long welcomed pilgrims, families and travellers seeking a clean, comfortable and convenient stay beside the Arabian Sea.
              </p>
              <p className="mt-4 leading-relaxed text-navy/70">
                Our accommodation is simple in spirit and generous in comfort: air-conditioned rooms with sea views, solar hot water, high-speed Wi-Fi, LED televisions and continuous power supply - cared for by a team that believes hospitality is a form of devotion.
              </p>
              <p className="mt-4 leading-relaxed text-navy/70">
                Whether you arrive for darshan at the Jyotirlinga, a family holiday on the Gujarat coast, or a quiet break by the waves, our promise is the same: a comfortable stay and a memorable Gujarat experience.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full border border-gold" />
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/e4w9ityv_IMG_0890.jpeg"
              alt="Sagar Darshan Guest House building and lawns, Somnath" loading="lazy" className="relative aspect-[4/5] w-full object-cover" />
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <SectionHeading number="02" overline="What Defines Us" title="The Sagar Darshan Way" center />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="h-full border border-navy/10 bg-sand p-9">
                  <span className="font-display text-4xl italic text-gold-dark">0{i + 1}</span>
                  <p className="mt-5 font-display text-xl text-navy">{v.title}</p>
                  <p className="mt-3 text-sm leading-relaxed text-navy/60">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <img src="https://customer-assets-gfyr7b9c.emergentagent.net/job_somnath-coastal-stay/artifacts/0nsdopje_IMG_0888.jpeg"
              alt="Somnath Temple and sea promenade viewed from Sagar Darshan Guest House" loading="lazy" className="aspect-[4/3] w-full object-cover" />
          </Reveal>
          <div>
            <SectionHeading number="03" overline="Moments From The Temple" title="Darshan, Made Effortless" />
            <Reveal delay={0.15}>
              <p className="mt-7 leading-relaxed text-navy/70">
                The sacred Shree Somnath Jyotirlinga Temple sits just moments from our door. Our complimentary shuttle and cart service makes the journey easy for all guests - especially senior citizens - so your focus stays where it belongs.
              </p>
              <Link to="/book" data-testid="about-book-btn" className="btn-primary mt-9">Book Your Stay</Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
